import { formatSelect, formatDateParam } from 'utils/search/formatParams'
import { IOptions, IStringObj, IObj } from '../types/common.types'
import { STATIC_URL, Pathname, TypeCodes } from '../data'
import { URL_SITE } from 'constants/data'
import {
  themeTexts,
  lectorTexts,
  FilterTypeName,
  specialityTexts,
  Key,
  pageKeys,
} from './search-data'
import { BASE_URL } from '../url'
import { IInitParams, IConfig } from '../types/search.types'
import { IBasicCard } from '../types/card.types'

const Option = {
  article: 'Статьи',
  video: 'Лекции',
  clinic: 'Клинические рекомендации',
  podcast: 'Подкасты',
  interactive: 'Интерактивы',
}

const optionsLinks = {
  [Option.article]: `${URL_SITE}/library`,
  [Option.video]: `${URL_SITE}/library/video`,
  [Option.clinic]: `${URL_SITE}/library/clinic`,
  [Option.podcast]: `${URL_SITE}/library/podcast`,
  [Option.interactive]: `${URL_SITE}/library/interactive`,
}

const OptionAliases = [
  {
    option: Option.article,
    alias: 'article',
  },
  {
    option: Option.video,
    alias: 'video',
  },
  {
    option: Option.clinic,
    alias: 'clinic',
  },
  {
    option: Option.podcast,
    alias: 'podcast',
  },
  { option: Option.interactive, alias: 'interactive' },
]

const options = [Option.article, Option.video, Option.clinic, Option.podcast, Option.interactive]

const categories = [
  {
    label: 'Лекция',
    value: 1,
  },
  {
    label: 'Консилиум',
    value: 2,
  },
  {
    label: 'Mероприятие',
    value: 3,
  },
]

const baseParams = {
  [Key.SUBSCRIPTION]: [],
  [Key.SPECIALITIES]: [],
  [Key.TEXT]: '',
  [Key.DATES]: [],
  [Key.LECTURS]: [],
  [Key.PAGE]: '1',
}

const InitParams: IObj<IInitParams> = {
  [Option.article]: {
    ...baseParams,
    [Key.THEMES]: [],
    [Key.READ_TIME]: [],
  },
  [Option.video]: {
    ...baseParams,
    [Key.THEMES]: [],
    [Key.CATEGORY]: [],
    [Key.DURATION]: [],
  },
  [Option.clinic]: {
    ...baseParams,
  },
  [Option.podcast]: {
    ...baseParams,
  },
  [Option.interactive]: {
    ...baseParams,
  },
}

const InitValues = {
  params: {
    [Option.article]: InitParams[Option.article],
    [Option.video]: InitParams[Option.video],
    [Option.clinic]: InitParams[Option.clinic],
    [Option.podcast]: InitParams[Option.podcast],
    [Option.interactive]: InitParams[Option.interactive],
  },
  pages: {
    [Option.article]: { [pageKeys.TOTAL]: 0, [pageKeys.CURRENT]: 0 },
    [Option.video]: { [pageKeys.TOTAL]: 0, [pageKeys.CURRENT]: 0 },
    [Option.clinic]: { [pageKeys.TOTAL]: 0, [pageKeys.CURRENT]: 0 },
    [Option.podcast]: { [pageKeys.TOTAL]: 0, [pageKeys.CURRENT]: 0 },
    [Option.interactive]: { [pageKeys.TOTAL]: 0, [pageKeys.CURRENT]: 0 },
  },
  cards: {
    [Option.article]: [] as IBasicCard[],
    [Option.video]: [] as IBasicCard[],
    [Option.clinic]: [] as IBasicCard[],
    [Option.podcast]: [] as IBasicCard[],
    [Option.interactive]: [] as IBasicCard[],
  },
  allCount: {
    [Option.article]: 0,
    [Option.video]: 0,
    [Option.clinic]: 0,
    [Option.podcast]: 0,
    [Option.interactive]: 0,
  },
}

const generateConfig = (
  lectures: IOptions[],
  themes: IOptions[],
  specialities: IOptions[],
  isTarget: boolean
): IObj<IConfig> => {
  const input = {
    placeholder: 'Поиск по названию...',
    key: Key.TEXT,
    sideIconPath: `${STATIC_URL}/filters/search-icon.svg`,
  }
  const basicFields = [
    {
      inputType: FilterTypeName.CALENDAR,
      title: 'Дата',
      key: Key.DATES,
      maxDate: new Date(),
      formatter: formatDateParam,
    },
    {
      inputType: FilterTypeName.SELECT,
      themes: lectures,
      mobileSwitchBtnText: lectorTexts,
      title: 'Лектор',
      placeholder: 'Поиск по лекторам...',
      key: Key.LECTURS,
      formatter: formatSelect,
    },
    {
      inputType: FilterTypeName.SELECT,
      themes: themes,
      mobileSwitchBtnText: themeTexts,
      title: 'Тема',
      placeholder: 'Поиск по темам...',
      key: Key.THEMES,
      formatter: formatSelect,
    },
    !isTarget && {
      inputType: FilterTypeName.SELECT,
      themes: specialities,
      mobileSwitchBtnText: specialityTexts,
      title: 'Специальность',
      placeholder: 'Поиск по специальностям...',
      key: Key.SPECIALITIES,
      formatter: formatSelect,
    },
  ]

  return {
    [Option.article]: {
      input,
      main: [...basicFields].filter((item) => item),
    },
    [Option.video]: {
      input,
      main: [...basicFields].filter((item) => item),
    },
    [Option.clinic]: {
      input,
      main: [
        {
          inputType: FilterTypeName.CALENDAR,
          title: 'Дата',
          key: Key.DATES,
          maxDate: new Date(),
          formatter: formatDateParam,
        },
      ].filter((item) => item),
    },
    [Option.podcast]: {
      input: {},
      main: [],
    },
    [Option.interactive]: {
      input: {},
      main: [],
    },
  }
}

const LibraryURL = {
  ARTICLES: `${BASE_URL.ARTICLE}?ArticleTypeId=${TypeCodes.ARTICLE}&count=true`,
  VIDEO: `${BASE_URL.ARTICLE}?ArticleTypeId=${TypeCodes.VIDEO}&count=true`,
  CLINIC: `${BASE_URL.ARTICLE}?ArticleTypeId=${TypeCodes.CLINIC}&count=true`,
  PODCAST: `${BASE_URL.PODCAST}?count=true`,
  INTERACTIVE: `${BASE_URL.INTERACTIVE}?count=true`,
}

const LinkComparasion: IStringObj = {
  [Option.article]: Pathname.ARTICLE,
  [Option.video]: Pathname.ARTICLE,
  [Option.clinic]: Pathname.ARTICLE,
  [Option.podcast]: Pathname.PODCAST,
  [Option.interactive]: Pathname.INTERACTIVE,
}

export {
  InitParams,
  generateConfig,
  Key,
  InitValues,
  pageKeys,
  LibraryURL,
  options,
  Option,
  LinkComparasion,
  categories,
  OptionAliases,
  optionsLinks,
}
